import { sceneUtils } from '@grafana/scenes';
import { type Spec as DashboardV2Spec } from '@grafana/schema/apis/dashboard.grafana.app/v2';
import { AnnoKeyMessage } from 'app/features/apiserver/types';
import { type DecoratedRevisionModel } from 'app/features/dashboard/types/revisionModels';
import { CustomDashboardTemplateInteractions } from 'app/features/dashboard-scene/analytics/dashboard-templates/main';
import { type DashboardScene } from 'app/features/dashboard-scene/scene/DashboardScene';
import { transformSaveModelSchemaV2ToScene } from 'app/features/dashboard-scene/serialization/transformSaveModelSchemaV2ToScene';
import { type DashboardTemplateExtensionHooks } from 'app/features/dashboard-scene/settings/enterprise-components/DashboardTemplateExtension';
import { transformTemplateToSaveModelSchemaV2 } from 'app/features/dashboard-scene/utils/dashboardTemplateEnvelope';
import { dispatch } from 'app/store/store';

import { dashboardtemplatesAPIv1alpha1 } from '../api/clients/dashboardtemplates/v1alpha1';

import { buildDashboardTemplateBody, preserveOuterSpec } from './wire';

export const dashboardTemplateExtension: DashboardTemplateExtensionHooks = {
  async loadTemplate(uid) {
    const result = await dispatch(dashboardtemplatesAPIv1alpha1.endpoints.getDashboardTemplate.initiate({ name: uid }));

    if ('error' in result && result.error) {
      throw result.error;
    }
    if (!result.data) {
      throw new Error('Template not found');
    }

    // Deep-clone before returning. RTKQ's Redux state is frozen by Immer in dev mode,
    // and the downstream V2 scene transformer mutates the dashboard spec in-place
    // (attaching panel refs, resolving datasource links, etc.).
    return structuredClone(result.data);
  },

  async listHistory(uid, { limit, continueToken }) {
    const result = await dispatch(
      dashboardtemplatesAPIv1alpha1.endpoints.listDashboardTemplate.initiate({
        labelSelector: 'grafana.app/get-history=true',
        fieldSelector: `metadata.name=${uid}`,
        limit,
        ...(continueToken ? { continue: continueToken } : {}),
      })
    );

    if ('error' in result && result.error) {
      throw result.error;
    }

    return result.data ?? { kind: '', apiVersion: '', metadata: { resourceVersion: '0' }, items: [] };
  },

  async restore(scene: DashboardScene, version: DecoratedRevisionModel): Promise<boolean> {
    const meta = scene.state.meta;
    const dashboardTemplateUid = meta.dashboardTemplateUid;

    if (!dashboardTemplateUid) {
      return false;
    }

    // Read the current outer template spec fields live via RTKQ rather than a cached snapshot
    // on DashboardMeta — single source of truth, picks up any concurrent admin edits.
    const getResult = await dispatch(
      dashboardtemplatesAPIv1alpha1.endpoints.getDashboardTemplate.initiate({ name: dashboardTemplateUid })
    );

    if ('error' in getResult && getResult.error) {
      return false;
    }
    const currentTemplate = getResult.data;
    if (!currentTemplate) {
      return false;
    }
    const outerSpec = currentTemplate.spec;

    // Title, description, tags, and dashboardVersion are outer spec fields and are not
    // part of version history (which only snapshots `spec.dashboard`). Preserve them
    // from the current outer spec, and swap the embedded dashboard with the historical
    // snapshot carried by `version.data`.
    const body = buildDashboardTemplateBody({
      name: dashboardTemplateUid,
      resourceVersion: currentTemplate.metadata?.resourceVersion,
      annotations: { [AnnoKeyMessage]: `Restored from version ${version.version}` },
      spec: preserveOuterSpec(outerSpec, version.data),
    });

    const result = await dispatch(
      dashboardtemplatesAPIv1alpha1.endpoints.replaceDashboardTemplate.initiate({
        name: dashboardTemplateUid,
        dashboardTemplate: body,
      })
    );

    if ('error' in result && result.error) {
      return false;
    }

    const response = result.data;
    if (!response) {
      return false;
    }

    const envelope = transformTemplateToSaveModelSchemaV2({
      dashboardSpec: response.spec.dashboard as DashboardV2Spec,
      resourceVersion: response.metadata?.resourceVersion,
      canEdit: meta.canEdit,
      canSave: meta.canSave,
    });

    const restoredScene = transformSaveModelSchemaV2ToScene(envelope);
    const newState = sceneUtils.cloneSceneObjectState(restoredScene.state);

    newState.meta = {
      ...newState.meta,
      isDashboardTemplate: true,
      dashboardTemplateUid,
      k8s: {
        ...newState.meta.k8s,
        resourceVersion: response.metadata?.resourceVersion ?? meta.k8s?.resourceVersion,
      },
    };

    scene.setState(newState);
    scene.exitEditMode({ skipConfirm: true, restoreInitialState: false });

    CustomDashboardTemplateInteractions.versionRestored({
      templateUid: dashboardTemplateUid,
      version: version.version,
    });

    return true;
  },
};
